import { type DisplayStatusType } from '@constants'
import { type ValueOf } from '@dni-types/utilities/value-of'
import { type Nullable } from '../nullable'
import { type ShippingLabelProvider } from './fpOrder'

/**
 * normal 官網一般訂單列表
 * campaign 活動訂單列表
 * service_order 官網寄倉訂單列表
 * subscribe 官網訂閱訂單列表
 * payment_transaction 依據交易層級回傳訂單列表（目前只有業務專站用的訂單列表用）
 */
export const WP_ORDER_TYPE = {
  NORMAL: 'normal',
  SUBSCRIBE: 'subscribe',
  PAYMENT_TRANSACTION: 'payment_transaction',
  SERVICE_ORDER: 'service_order',
  CAMPAIGN: 'campaign',
} as const

export type WpOrderType = ValueOf<typeof WP_ORDER_TYPE>

export const WP_ORDER_STATUS = {
  /** 全部 */
  ALL: 'all_statuses',
  /** 待付款 */
  PENDING: 'pending',
  /** 待出貨 */
  PROCESSING: 'processing',
  /** 已出貨 */
  COMPLETED: 'completed',
  /** 取消 */
  CANCELLED: 'cancelled',
  /** 寄倉的預產訂單 */
  UNCREATED: 'uncreated',
  /** 準備出貨 */
  READY_FOR_SHIPMENT: 'ready-for-shipment',
  /** 已退貨 */
  REFUNDED: 'refunded',
} as const

export type WpOrderStatus = ValueOf<typeof WP_ORDER_STATUS>

export const SUBSCRIPTION_STATUS = {
  /** 有自動續訂 */
  ACTIVE: 'active',
  /** 取消續訂 */
  CANCELLED: 'cancelled',
  SUSPENDED: 'suspended',
  /** 付款失敗 */
  RENEWAL_FAILED: 'renewal failed',
  /** 付款失敗 */
  WAIT_FOR_RETRIED: 'wait for retried',
  /** 沒有自動續訂 */
  FINISHED: 'finished',
} as const

export type SubscriptionStatus = ValueOf<typeof SUBSCRIPTION_STATUS>

export const ORIGINAL_ORDER_STATUS = {
  /** 預購訂單 */
  ON_HOLD: 'on-hold',
  /** 預購訂單 */
  ON_HOLD_1: 'on-hold-1',
  /** 預購訂單 */
  ON_HOLD_2: 'on-hold-2',
  PENDING: 'pending',
  PROCESSING: 'processing',
  BOXEDN: 'boxedn',
  ALREDYBOXEDN: 'alredyboxedn',
  PACKAGE_N1: 'package-n1',
  PACKAGE_N2: 'package-n2',
  COMPLETED: 'completed',
  PRECREATE: 'precreate',
} as const

export type OriginalOrderStatus = ValueOf<typeof ORIGINAL_ORDER_STATUS>

export const REFUND_STATUS = {
  MANUAL_REFUND: 'manual_refund',
  REFUNDED: 'refunded',
  REFUND_FAILED: 'refund_failed',
  NO_REFUND: 'no_refund',
} as const

export type RefundStatus = ValueOf<typeof REFUND_STATUS>

export const PAYMENT_TRANSACTION_STATUS = {
  PENDING: 'pending',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  FAILED: 'failed',
  REFUNDING: 'refunding',
  REFUNDED: 'refunded',
} as const

export type PaymentTransactionStatus = ValueOf<typeof PAYMENT_TRANSACTION_STATUS>

export type ECommerceOrder = {
  order_type: typeof WP_ORDER_TYPE.PAYMENT_TRANSACTION
  transaction_hash: string
  currency: string
  /** fastpass 交易編號 */
  payment_transaction_id: string
  payment_transaction_status?: PaymentTransactionStatus | null
  subscription?: Subscription
  /** 一般訂單時為 null */
  delivery_service?: DeliveryService
  related_orders?: OrderInfo[]
  /** From payment transaction */
  total: number
  /** orders_sum_total是經內部人員修改後的訂單金額 */
  orders_total_sum?: number
  campaign_id: number
  is_payable: boolean
  order_info: OrderInfo
  refund_status: RefundStatus | null
} | {
  order_type: string
  transaction_hash: string
  currency: string
  /** fastpass 交易編號 */
  payment_transaction_id: null
  payment_transaction_status?: PaymentTransactionStatus | null
  order_info: OrderInfo
  /** 一般訂單時為 null */
  subscription?: Subscription
  /** 一般訂單時為 null */
  delivery_service?: DeliveryService
  related_orders?: OrderInfo[]
  /** From payment transaction */
  total: number
  /** orders_sum_total是經內部人員修改後的訂單金額 */
  orders_total_sum?: number
  campaign_id: number | null
  /** 是否可付款 */
  is_payable: boolean
  last4: string | null
  payment_token_id: number | null
  refund_status: RefundStatus | null
}

export interface DeliveryService {
  order_id: string
  /** 每次配送的單位 e.g. day, week, month, year */
  period_unit: string
  /** 每次配送的值 */
  period_value: number
  /** NOTE: 目前 service_order_items 長度只有 1，若超過 1 則可能為贈品 */
  service_order_items: ServiceOrderItem[]
  remain_delivery_at: string[]
}

export interface ServiceOrderItem {
  product_id: number
  /** 寄倉商品 id，用於更新寄倉配送資訊 */
  parent_product_id?: number
  total_quantity: number
  remain_quantity: number
  quantity_per_delivery: number
  product_name: string
}

export interface OrderInfo {
  id: number
  order_id: string
  status: WpOrderStatus
  /** 前台顯示用狀態（pending_payment / pending_shipment / preparing_shipment / shipped / cancelled / refunded） */
  display_status?: DisplayStatusType
  order_created_at: string
  /** 是否為寄倉、訂閱訂單的子訂單 */
  is_child_order: boolean
  /** 是否為預產訂單狀態 */
  is_precreate_status: boolean
  /** NOTE: 寄倉、訂閱訂單目前 order_items 長度只有 1，一般訂單則為多筆 */
  order_items: OrderItem[]
  delivery_date?: string
  /** WordPress slug */
  original_status: OriginalOrderStatus
  shipping_label_providers: ShippingLabelProvider[]
}

export interface OrderItem {
  /** NOTE: 如果是寄倉、訂閱，則為寄倉商品「服務」的 id */
  product_id: number
  variation_id: number
  product_name: string
  variation_name: string
  product_image: string
  /** 2B 站台顯示用的商品圖，取自 gallery 最後一張 */
  last_gallery_image?: string
  quantity: number
  total: number
  is_giveaway: 0 | 1
  parent_product_id: Nullable<number>
}

export interface Subscription {
  status: SubscriptionStatus
  /** 是否自動續訂 */
  is_auto_renew: boolean
  /** 每次配送的單位 e.g. day, week, month, year */
  period_unit: string
  /** 每次配送的值 */
  period_value: number
  next_payment_at: string
  /** 自動扣款日 */
  charge_at: string
  /** 續訂扣款失敗次數 */
  payment_failed_count: number
}

export interface UpdatedOrders {
  order_ids?: string[]
  cache_timestamp?: string
}
